import { useState } from 'react';
import {
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Table,
  Button,
  Modal,
  ScrollArea,
  Loader,
  Center,
  Alert,
  Select,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { useGetLLMRequests, LLMRequestLog } from '../../api/llmRequests';

/**
 * Component to display LLM request logs
 * Shows all API requests sent to Claude/OpenAI/etc with prompts, responses, and token usage
 * Useful for debugging and transparency
 */
export function LLMRequestsPanel() {
  const { data: requests = [], isLoading, error } = useGetLLMRequests(100);
  const [selectedRequest, setSelectedRequest] = useState<LLMRequestLog | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Filter requests by status if filter is set
  const filteredRequests = statusFilter
    ? requests.filter((r) => r.status === statusFilter)
    : requests;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending_execution':
        return <IconClock size={16} />;
      case 'executing':
        return <Loader size={16} />;
      case 'completed':
        return <IconCheck size={16} />;
      case 'failed':
        return <IconX size={16} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_execution':
        return 'yellow';
      case 'executing':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatTokenCount = (count: number) => {
    return count.toLocaleString();
  };

  return (
    <Paper p="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <div>
          <Group justify="space-between" align="center">
            <div>
              <Title order={2} size="h3">
                LLM Request Logs
              </Title>
              <Text size="sm" c="dimmed">
                Transparent logging of all API requests sent for resume generation
              </Text>
            </div>
          </Group>
        </div>

        {/* Error State */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load request logs: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : filteredRequests.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            No LLM requests yet. Generate a resume with AI to see request logs here.
          </Alert>
        ) : (
          <>
            {/* Status Filter */}
            <Group gap="md">
              <Select
                placeholder="Filter by status"
                clearable
                searchable
                value={statusFilter}
                onChange={setStatusFilter}
                data={[
                  { value: 'pending_execution', label: 'Pending' },
                  { value: 'executing', label: 'Executing' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'failed', label: 'Failed' },
                ]}
                style={{ flex: 1, maxWidth: 200 }}
              />
              <Text size="sm" c="dimmed">
                {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
              </Text>
            </Group>

            {/* Requests Table */}
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th></Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Artifact Type</Table.Th>
                    <Table.Th>Provider</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th>Tokens</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredRequests.map((request) => (
                    <Table.Tr key={request.id}>
                      <Table.Td style={{ cursor: 'pointer' }}>
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => setSelectedRequest(request)}
                          rightSection={<IconChevronRight size={14} />}
                          p={0}
                          h="auto"
                        >
                          View
                        </Button>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {getStatusIcon(request.status)}
                          <Badge size="sm" color={getStatusColor(request.status)}>
                            {request.status.replace('_', ' ')}
                          </Badge>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{request.artifactType}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{request.providerId}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {formatDate(request.createdAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {request.tokenUsage
                            ? `${formatTokenCount(request.tokenUsage.total)}`
                            : '-'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </>
        )}
      </Stack>

      {/* Request Detail Modal */}
      <Modal
        opened={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        title="LLM Request Details"
        size="lg"
        scrollAreaComponent={ScrollArea.Autosize}
      >
        {selectedRequest && (
          <Stack gap="md">
            {/* Status & Metadata */}
            <div>
              <Group justify="space-between" align="flex-start" mb="md">
                <div>
                  <Text fw={500} mb="xs">
                    Status
                  </Text>
                  <Group gap="xs">
                    {getStatusIcon(selectedRequest.status)}
                    <Badge size="lg" color={getStatusColor(selectedRequest.status)}>
                      {selectedRequest.status.replace('_', ' ')}
                    </Badge>
                  </Group>
                </div>
                <div>
                  <Text fw={500} size="sm" mb="xs">
                    Timing
                  </Text>
                  <Stack gap="xs">
                    <div>
                      <Text size="xs" c="dimmed">
                        Created
                      </Text>
                      <Text size="sm">{formatDate(selectedRequest.createdAt)}</Text>
                    </div>
                    {selectedRequest.executedAt && (
                      <div>
                        <Text size="xs" c="dimmed">
                          Executed
                        </Text>
                        <Text size="sm">{formatDate(selectedRequest.executedAt)}</Text>
                      </div>
                    )}
                    {selectedRequest.completedAt && (
                      <div>
                        <Text size="xs" c="dimmed">
                          Completed
                        </Text>
                        <Text size="sm">{formatDate(selectedRequest.completedAt)}</Text>
                      </div>
                    )}
                  </Stack>
                </div>
              </Group>
            </div>

            {/* Metadata */}
            <div>
              <Text fw={500} mb="xs">
                Metadata
              </Text>
              <Stack gap="xs">
                <div>
                  <Text size="xs" c="dimmed">
                    Artifact Type
                  </Text>
                  <Text size="sm">{selectedRequest.artifactType}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Provider
                  </Text>
                  <Text size="sm">{selectedRequest.providerId}</Text>
                </div>
                {selectedRequest.jobId && (
                  <div>
                    <Text size="xs" c="dimmed">
                      Job ID
                    </Text>
                    <Text size="sm" style={{ wordBreak: 'break-all' }}>
                      {selectedRequest.jobId}
                    </Text>
                  </div>
                )}
              </Stack>
            </div>

            {/* Provider Configuration */}
            {selectedRequest.providerConfig && (
              <div>
                <Text fw={500} mb="xs">
                  LLM Provider Configuration
                </Text>
                <Stack gap="xs">
                  <div>
                    <Text size="xs" c="dimmed">
                      Provider Name
                    </Text>
                    <Text size="sm">
                      <Badge size="sm">{selectedRequest.providerConfig.name.toUpperCase()}</Badge>
                    </Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">
                      Display Name
                    </Text>
                    <Text size="sm">{selectedRequest.providerConfig.displayName}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">
                      Model
                    </Text>
                    <Text size="sm">{selectedRequest.providerConfig.modelId}</Text>
                  </div>
                  {selectedRequest.providerConfig.config && (
                    <>
                      <div>
                        <Text size="xs" c="dimmed">
                          Temperature
                        </Text>
                        <Text size="sm">
                          {selectedRequest.providerConfig.config.temperature ?? 'Default (0.7)'}
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">
                          Max Tokens
                        </Text>
                        <Text size="sm">
                          {selectedRequest.providerConfig.config.maxTokens ?? 'Default (4096)'}
                        </Text>
                      </div>
                    </>
                  )}
                </Stack>
              </div>
            )}

            {/* API Request Payload */}
            {selectedRequest.apiRequest && (
              <div>
                <Text fw={500} mb="xs">
                  API Request Payload
                </Text>
                <Paper p="xs" radius="sm" bg="gray.8" style={{ maxHeight: 300, overflow: 'auto' }}>
                  <Text size="xs" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {JSON.stringify(selectedRequest.apiRequest, null, 2)}
                  </Text>
                </Paper>
              </div>
            )}

            {/* Token Usage */}
            {selectedRequest.tokenUsage && (
              <div>
                <Text fw={500} mb="xs">
                  Token Usage
                </Text>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm">Input Tokens</Text>
                    <Text size="sm" fw={500}>
                      {formatTokenCount(selectedRequest.tokenUsage.input)}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Output Tokens</Text>
                    <Text size="sm" fw={500}>
                      {formatTokenCount(selectedRequest.tokenUsage.output)}
                    </Text>
                  </Group>
                  <Group justify="space-between" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }} pt="xs">
                    <Text size="sm" fw={500}>
                      Total Tokens
                    </Text>
                    <Text size="sm" fw={500}>
                      {formatTokenCount(selectedRequest.tokenUsage.total)}
                    </Text>
                  </Group>
                </Stack>
              </div>
            )}

            {/* Request Body */}
            {selectedRequest.requestBody && (
              <div>
                <Text fw={500} mb="xs">
                  Request Input
                </Text>
                <Stack gap="xs">
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      Job Title
                    </Text>
                    <Paper p="xs" radius="sm" bg="gray.8">
                      <Text size="sm" style={{ wordBreak: 'break-word' }}>
                        {selectedRequest.requestBody.jobTitle}
                      </Text>
                    </Paper>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      Company
                    </Text>
                    <Paper p="xs" radius="sm" bg="gray.8">
                      <Text size="sm" style={{ wordBreak: 'break-word' }}>
                        {selectedRequest.requestBody.company}
                      </Text>
                    </Paper>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      Job Description (truncated)
                    </Text>
                    <Paper p="xs" radius="sm" bg="gray.8" style={{ maxHeight: 150, overflow: 'auto' }}>
                      <Text size="sm" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {selectedRequest.requestBody.jobDescription.substring(0, 500)}
                        {selectedRequest.requestBody.jobDescription.length > 500 ? '...' : ''}
                      </Text>
                    </Paper>
                  </div>
                </Stack>
              </div>
            )}

            {/* Compiled Prompt */}
            {selectedRequest.prompt && (
              <div>
                <Text fw={500} mb="xs">
                  Compiled Prompt Sent to API
                </Text>
                <Paper p="xs" radius="sm" bg="gray.8" style={{ maxHeight: 250, overflow: 'auto' }}>
                  <Text size="xs" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {selectedRequest.prompt}
                  </Text>
                </Paper>
              </div>
            )}

            {/* Result */}
            {selectedRequest.result && (
              <div>
                <Text fw={500} mb="xs">
                  Generated Resume (Result)
                </Text>
                <Paper p="xs" radius="sm" bg="gray.8" style={{ maxHeight: 250, overflow: 'auto' }}>
                  <Text size="xs" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {selectedRequest.result}
                  </Text>
                </Paper>
              </div>
            )}

            {/* Error Message */}
            {selectedRequest.errorMessage && (
              <Alert icon={<IconX size={16} />} color="red" title="Error">
                {selectedRequest.errorMessage}
              </Alert>
            )}
          </Stack>
        )}
      </Modal>
    </Paper>
  );
}
